import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, DrawerStepper, showToast } from '@mercai/clever'

import { SupplyPaymentForm } from '@/components'

import { schemaSupplyPayment } from '@/components/forms/supply-payment-form/supply-payment-form.constants'
import { useValidationErrors } from '@/helpers'
import {
  useSupplyPaymentCreate,
  useSupplyPaymentDetail,
  useSupplyPaymentEdit,
} from '@/services'
import { SupplyPaymentProps } from '@/types'
import { DrawerSupplyPaymentFormProps } from './drawer-supply-payment-form.type'

export const DrawerSupplyPaymentForm = ({
  isOpen,
  onClose,
  onSuccess,
  uuid,
  supplyOrderUuid,
  supplierUuid,
}: DrawerSupplyPaymentFormProps) => {
  const { t } = useTranslation('screenSupplyOrderDetail')

  const [values, setValues] = useState<Partial<SupplyPaymentProps>>({})

  const { data } = useSupplyPaymentDetail(uuid)

  const { mutateAsync: createMutateAsync, isPending: createIsPending } =
    useSupplyPaymentCreate()

  const { mutateAsync: editMutateAsync, isPending: editIsPending } =
    useSupplyPaymentEdit(uuid)

  const isPending = createIsPending || editIsPending

  const { errors, isValid, onHandleListenErrors } = useValidationErrors(
    schemaSupplyPayment(t),
    values,
  )

  const onHandleOpen = () => {
    onClose()
    setValues({})
  }

  const texts = {
    title: uuid
      ? t('drawerSupplyPaymentFormUpdateTitle')
      : t('drawerSupplyPaymentFormCreateTitle'),
    description: uuid
      ? t('drawerSupplyPaymentFormUpdateDescription')
      : t('drawerSupplyPaymentFormCreateDescription'),
    successTitle: uuid
      ? t('drawerSupplyPaymentFormUpdateSuccessTitle')
      : t('drawerSupplyPaymentFormCreateSuccessTitle'),
    successDescription: uuid
      ? t('drawerSupplyPaymentFormUpdateSuccessDescription')
      : t('drawerSupplyPaymentFormCreateSuccessDescription'),
  }

  const onSubmit = async () => {
    if (!isValid) {
      return onHandleListenErrors(true)
    }

    onHandleListenErrors(false)

    const request = uuid ? editMutateAsync : createMutateAsync

    const response = await request({
      supplyOrderUuid,
      supplierUuid,
      ...values,
      value: `${values.value || 0}` as unknown as number,
      statements: undefined,
      uuid: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    })

    if (!response.success) {
      return showToast({
        variant: 'error',
        title: response?.message,
        position: 'bottom-left',
      })
    }

    showToast({
      variant: 'success',
      title: texts.successTitle,
      content: texts.successDescription,
      position: 'bottom-left',
    })

    onSuccess?.()
    onHandleOpen()
  }

  useEffect(() => {
    if (data?.data) {
      setValues({
        ...data.data,
      })
    } else {
      setValues({})
    }
  }, [data])

  return (
    <DrawerStepper
      title={texts?.title}
      description={texts?.description}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      initialStep="root"
      translations={{
        optional: '',
      }}
      steps={{
        root: {
          isOptional: false,
          backNavigate: '',
          component: () => (
            <SupplyPaymentForm
              values={values}
              onChangeValues={setValues}
              errors={errors}
            />
          ),
          footer: () => (
            <>
              <Button variant="secondary" onClick={onHandleOpen}>
                {t('backButton')}
              </Button>
              <Button
                variant="primary"
                onClick={onSubmit}
                disabled={isPending}
                isLoading={isPending}
              >
                {t('confirmButton')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
